import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavTab {
  id: string;
  icon: string;
  title: string;
  titleEn: string;
  to: string;
}

const NAV_TABS: NavTab[] = [
  { id: 'introduction', icon: 'ri-file-text-line', title: '成果简介', titleEn: 'Introduction', to: '/achievement/001?tab=introduction' },
  { id: 'video', icon: 'ri-video-line', title: '成果视频', titleEn: 'Videos', to: '/achievement/001?tab=video' },
  { id: 'application', icon: 'ri-article-line', title: '申报书', titleEn: 'Application', to: '/achievement/001?tab=application' },
  { id: 'certification', icon: 'ri-shield-check-line', title: '成果鉴定', titleEn: 'Appraisal', to: '/achievement/001?tab=certification' },
  { id: 'report', icon: 'ri-bar-chart-box-line', title: '成果报告', titleEn: 'Report', to: '/achievement/001?tab=report' },
  { id: 'materials', icon: 'ri-folder-2-line', title: '支撑材料', titleEn: 'Materials', to: '/achievement/001?tab=materials' },
];

interface SubNavbarProps {
  isInline?: boolean;
}

export default function SubNavbar({ isInline = false }: SubNavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const location = useLocation();

  const isTabActive = (tab: NavTab) => {
    const currentPath = location.pathname + location.search;
    return tab.to === currentPath;
  };

  useEffect(() => {
    if (isInline) return;
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 80);
      setVisible(y > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInline]);

  const renderNavItems = () => (
    <div className="flex items-center gap-3 h-full py-2">
      {NAV_TABS.map((tab) => {
        const isActive = isTabActive(tab);
        const isHovered = hovered === tab.id;
        return (
          <Link
            key={tab.id}
            to={tab.to}
            onMouseEnter={() => setHovered(tab.id)}
            onMouseLeave={() => setHovered(null)}
            className={`relative flex items-center gap-3 px-6 py-3 rounded-full whitespace-nowrap cursor-pointer transition-all duration-300 ${
              isActive
                ? 'bg-primary-500 text-white scale-[1.03]'
                : isHovered
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            <div className="w-7 h-7 flex items-center justify-center">
              <i className={`${tab.icon} text-[18px]`} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[16px] font-extrabold font-heading">
                {tab.title}
              </span>
              <span className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${
                isActive ? 'text-white/70' : isHovered ? 'text-primary-400' : 'text-foreground-400'
              }`}>
                {tab.titleEn}
              </span>
            </div>
          </Link>
        );
      })}
    </div>
  );

  if (isInline) {
    return (
      <nav className="relative bg-white border-b border-background-200/60">
        <div className="max-w-[1440px] mx-auto h-[68px] flex items-center justify-center px-4 md:px-8">
          {renderNavItems()}
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`fixed top-[64px] left-0 right-0 z-40 transition-all duration-500 h-[68px] bg-white/95 backdrop-blur-sm border-b border-background-200/60 ${
        visible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      }`}
      style={{
        boxShadow: scrolled
          ? '0 2px 12px rgba(0,0,0,0.06)'
          : '0 1px 2px rgba(0,0,0,0.03)',
      }}
    >
      <div className="max-w-[1440px] mx-auto h-full flex items-center justify-center px-4 md:px-8">
        {renderNavItems()}
      </div>
    </nav>
  );
}